import { Suspense, useEffect, useRef, useState } from 'react';
import { init, skipTutorial } from './store/tutorialSlice';
import {
  selectTutorialReady,
  selectTutorialStep,
  selectTutorialFinished,
  selectBackground,
} from './store/selectors';
import { useAppDispatch, useAppSelector } from '@app/store';
import { stepsList, preloadStep } from './pages/mainStage/consts';
import styles from './tutorial.module.scss';
import { Background } from './components/Background';
import { Loading } from '@shared/Loading';

const LoadingTutorial = () => {
  return (
    <>
      <Background />
      <div className={styles.loadingWrapper}>
        <Loading />
      </div>
    </>
  );
};

export const InteractiveTutorial = () => {
  const dispatch = useAppDispatch();

  const ready = useAppSelector(selectTutorialReady);
  const step = useAppSelector(selectTutorialStep);
  const finished = useAppSelector(selectTutorialFinished);
  const [displayedStep, setDisplayedStep] = useState(step);
  const [isLoading, setIsLoading] = useState(false);
  const background = useAppSelector(selectBackground);

  const loadedStepsRef = useRef(new Set<number>());

  useEffect(() => {
    dispatch(init());
  }, [dispatch]);

  useEffect(() => {
    preloadStep(displayedStep)?.then(() => {
      loadedStepsRef.current.add(displayedStep);
    });
  }, [displayedStep]);

  useEffect(() => {
    const index = step - 1;
    if (loadedStepsRef.current.has(index)) {
      setDisplayedStep(step);
      return;
    }
    setIsLoading(true);
    preloadStep(index)?.then(() => {
      loadedStepsRef.current.add(index);
      setIsLoading(false);
      setDisplayedStep(step);
    });
  }, [step]);

  if (!ready || finished) return null;

  const StepComponent = stepsList[displayedStep - 1];

  if (!StepComponent) {
    dispatch(skipTutorial());
    return null;
  }

  return (
    <div className={styles.tutorial} data-tutorial="tutorial">
      {isLoading ? (
        <LoadingTutorial />
      ) : (
        <Suspense fallback={<Background />}>
          <StepComponent />
        </Suspense>
      )}
      {background && <Background />}
    </div>
  );
};
